import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Typography, Tag, Spin, message } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import SubscriptionBanner from "../../assets/images/bg/subscription_home-logo.svg";
import { checkoutSubscription, getAvailablePlans } from "../../components/redux/subscriptionSlice";
import PurchaseHistoryModal from "./PurchaseHistoryModal";
import "./subscription.css";

const { Title, Text } = Typography;

const currency = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" });

const NOTE =
  "Next steps may include lifestyle and nutrition guidance, recommendations for ongoing care with My Fertility Labs, where prescription treatment may be considered when clinically appropriate, or referral to a fertility clinic or other specialist where indicated.";

const planTotal = (plan) => Math.round(plan.monthlyAmount * plan.noMonths * 100) / 100;

const monthsLabel = (n) => `${n} month${n === 1 ? "" : "s"}`;

const isFertilityScreen = (plan) => String(plan.name || "").toLowerCase().includes("fertility screen");

function resolveGender(data) {
  const raw = data?.gender || data?.Gender || data?.sex || data?.Sex || data?.patientGender;
  if (!raw || typeof raw !== "string") return null;
  const val = raw.toLowerCase().trim();
  if (val.startsWith("m")) return "male";
  if (val.startsWith("f")) return "female";
  return null;
}

function FertilityScreenCard({ plan, loading, disabled, onSelect }) {
  return (
    <div className="cp-card">
      <div className="cp-card-header cp-card-header--blue">{plan.name.toUpperCase()}</div>
      <div className="cp-card-body">
        <Tag color="blue" style={{ marginBottom: 12 }}>
          {monthsLabel(plan.noMonths)} of care
        </Tag>
        <p style={{ margin: "0 0 8px" }}>
          {plan.description ||
            "Designed for patients seeking a comprehensive fertility assessment clarity on next steps."}
        </p>
        <Text type="secondary" italic style={{ display: "block", fontSize: 12, marginTop: 12 }}>
          {NOTE}
        </Text>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20 }}>
          <Text strong style={{ fontSize: 22 }}>
            {currency.format(planTotal(plan))}
          </Text>
          <Button
            type="primary"
            style={{ background: "#00ADEF", borderColor: "#00ADEF" }}
            loading={loading}
            disabled={disabled}
            onClick={() => onSelect(plan)}
          >
            Select
          </Button>
        </div>
      </div>
    </div>
  );
}

function PlanColumn({ plan, disabled, onSelect }) {
  return (
    <div className="cp-bundle-col">
      <div className="cp-bundle-title">{plan.name.toUpperCase()}</div>
      {!!plan.description && (
        <Text type="secondary" style={{ display: "block", fontSize: 12, margin: "0 0 8px" }}>
          {plan.description}
        </Text>
      )}
      <button type="button" className="cp-bundle-row" disabled={disabled} onClick={() => onSelect(plan)}>
        <span>{monthsLabel(plan.noMonths)}</span>
        <span className="cp-bundle-row-right">
          {currency.format(planTotal(plan))}
          <RightOutlined style={{ fontSize: 11, marginLeft: 8 }} />
        </span>
      </button>
    </div>
  );
}

export default function CarePlans() {
  const dispatch = useDispatch();
  const { userAuth } = useSelector((state) => state.authentication);
  const gender = resolveGender(userAuth?.obj ?? userAuth);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasingId, setPurchasingId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    dispatch(getAvailablePlans()).then((result) => {
      if (getAvailablePlans.fulfilled.match(result)) {
        setPlans(Array.isArray(result.payload) ? result.payload : []);
      } else {
        message.error(result.payload?.message || "Could not load care plans.");
      }
      setLoading(false);
    });
  }, [dispatch]);

  const screens = useMemo(() => plans.filter(isFertilityScreen), [plans]);
  const otherPlans = useMemo(() => plans.filter((p) => !isFertilityScreen(p)), [plans]);
  const genderLabel = gender === "male" ? "Male" : gender === "female" ? "Female" : "";

  const handleSelect = async (plan) => {
    setPurchasingId(plan.id);
    const result = await dispatch(checkoutSubscription(plan.id));
    if (checkoutSubscription.fulfilled.match(result) && result.payload) {
      window.location.href = result.payload;
      return;
    }
    message.error(result.payload?.message || result.error?.message || "Could not start checkout.");
    setPurchasingId(null);
  };

  return (
    <div className="cp-wrapper">
      <div className="cp-top">
        <div>
          <Title level={4} style={{ color: "#335CAD", marginBottom: 4 }}>
            FERTILITY CARE PLANS
          </Title>
          <Text type="secondary">Choose the level of care that best supports your journey.</Text>
          <br />
          <Text type="secondary">Appointments remain available in your account for 24 months from the date of purchase.</Text>
        </div>
        <img src={SubscriptionBanner} alt="" className="cp-top-illustration" />
      </div>

      <div className="cp-section-heading">
        <span>{genderLabel.toUpperCase()} CARE PLANS</span>
        <a
          className="cp-credit-link"
          onClick={(e) => {
            e.preventDefault();
            setHistoryOpen(true);
          }}
          href="#purchase-history"
        >
          View purchase history <RightOutlined style={{ fontSize: 10 }} />
        </a>
      </div>
      <PurchaseHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />

      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          {screens.map((plan) => (
            <Col xs={24} md={12} key={plan.id}>
              <FertilityScreenCard
                plan={plan}
                loading={purchasingId === plan.id}
                disabled={purchasingId !== null}
                onSelect={handleSelect}
              />
            </Col>
          ))}
        </Row>

        {otherPlans.length > 0 && (
          <>
            <div className="cp-section-heading" style={{ marginTop: 32 }}>
              <span>MORE CARE OPTIONS</span>
            </div>

            <div className="cp-bundles">
              {otherPlans.map((plan) => (
                <PlanColumn key={plan.id} plan={plan} disabled={purchasingId !== null} onSelect={handleSelect} />
              ))}
            </div>
          </>
        )}
      </Spin>
    </div>
  );
}
